<template>
  <div>
    <v-card :loading="loading" :disabled="loading">
      <v-card-title>
        {{ $t("registration.registration_system_for_officer") }}
      </v-card-title>
      <v-card-text>
        <v-form v-model="form_validate_status" ref="form">
          <v-divider color="white"></v-divider>

          <v-row class="mt-2 mx-auto">
            <v-col cols="12" sm="6" md="3" lg="2">
              <v-select
                label="ปีการศึกษา"
                v-model="selected_year"
                dense
                outlined
                :items="year_list"
                :item-value="(item) => item"
                :rules="year_rules"
                required
                @change="onCriteriaChange"
              ></v-select
            ></v-col>
            <v-col cols="12" sm="6" md="3" lg="2">
              <v-select
                label="ภาคการศึกษา"
                v-model="selected_semester"
                dense
                outlined
                :items="semester_list"
                :item-value="(item) => item"
                :rules="semester_rules"
                required
                @change="onCriteriaChange"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3" lg="2">
              <v-text-field
                label="รหัสนักศึกษา"
                outlined
                dense
                v-model="studentIdInput"
                height="40px"
                :rules="student_id_rules"
                required
                @keyup.enter="fetch_student"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="3" lg="1">
              <v-btn
                color="primary"
                class="white--text mr-3 mb-2"
                :block="$vuetify.breakpoint.xsOnly"
                @click="fetch_student"
              >
                <v-icon left>mdi-magnify</v-icon>
                {{ $t("teach_table.search") }}
              </v-btn>
            </v-col>
            <v-col cols="12" sm="6" md="3" lg="3">
              <v-chip
                v-if="isFutureInnovationStudent"
                class="mt-1 pl-5 pr-5 purple--text font-weight-bold"
                color="pink lighten-4"
                >เป็นนักศึกษา 4+1 ด้วย</v-chip
              >
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-card-text class="text-center pb-5" v-if="error">
        <h2>มีบางอย่างผิดพลาด</h2>
      </v-card-text>
    </v-card>

    <Registration
      :key="`registration-${registrationKey}`"
      class="mt-5"
      v-if="canDisplayRegistration && canUseRegistration"
      :clear_student="clear_student"
      :fetch_student="fetch_student"
      :selected_year="selected_year"
      :selected_semester="selected_semester"
      :selected_student_id="activeStudentId"
      :selected_student_info="studentInfo"
      :bypass="true"
    />

    <RegistrationX
      :key="`registration-x-${registrationKey}`"
      class="mt-5"
      v-if="canDisplayRegistration && canUseRegistrationX"
      :clear_student="clear_student"
      :fetch_student="fetch_student"
      :selected_year="selected_year"
      :selected_semester="selected_semester"
      :selected_student_id="activeStudentId"
      :selected_student_info="studentInfo"
      :selected_is_future_innovation_student="isFutureInnovationStudent"
      :bypass="true"
    />
  </div>
</template>

<script>
import * as dayjs from "dayjs";
require("dayjs/locale/th");
import Registration from "./Registration.vue";
import RegistrationX from "./RegistrationX.vue";
import RegistrationProvider from "@/services/registration.provider.js";
import UserCentralProvider from "@/services/user.central.provider.js";

const semesters = ["1", "2", "3"].sort((a, b) => b - a);
const RegistrationService = new RegistrationProvider();
const UserCentralService = new UserCentralProvider();

export default {
  name: "registration_for_officer",
  components: {
    Registration,
    RegistrationX,
  },
  data: () => ({
    loading: false,
    error: false,

    year_list: [],
    semester_list: semesters,

    form_validate_status: false,
    selected_year: null,
    selected_semester: null,
    studentIdInput: "",
    activeStudentId: null,
    studentInfo: null,
    isFutureInnovationStudent: false,
    registrationKey: 0,
    year_rules: [(v) => !!v || "Year is required"],
    semester_rules: [(v) => !!v || "Semester is required"],
    student_id_rules: [
      (v) => !!v || "Student ID is required",
      (v) =>
        !v ||
        String(v).length == 8 ||
        "Student ID must be equal 8 characters",
    ],
  }),
  computed: {
    studentId() {
      return this.studentIdInput;
    },
    studentPrivilege() {
      return (
        this.studentInfo &&
        this.studentInfo.payload &&
        this.studentInfo.payload.ticket &&
        this.studentInfo.payload.ticket.privilege
      );
    },
    canUseRegistration() {
      return this.studentPrivilege == 80;
    },
    canUseRegistrationX() {
      return this.studentPrivilege == 85 || this.isFutureInnovationStudent;
    },
    canDisplayRegistration() {
      return (
        this.form_validate_status &&
        this.activeStudentId &&
        this.activeStudentId === this.studentId &&
        this.studentInfo
      );
    },
  },
  methods: {
    load_year() {
      const maxYear = parseInt(dayjs().format("BBBB"));
      for (let year = 2563; year <= maxYear; year++) this.year_list.push(String(year));
      this.year_list.sort().reverse();
    },

    syncQuery() {
      this.$router
        .replace({
          name: "registration_for_officer",
          query: {
            ...this.$route.query,
            student_id: this.activeStudentId,
            year: this.selected_year,
            semester: this.selected_semester,
          },
        })
        .catch(() => {});
    },

    onCriteriaChange() {
      this.fetchWhenStudentIdReady();
    },

    async fetch_student() {
      if (!this.$refs.form.validate() || !this.studentId) return;

      const studentId = this.studentId;
      this.loading = true;
      this.error = false;
      this.clear_student();

      try {
        const studentInfo = await UserCentralService.getAuthUserInfo(studentId);
        const futureInnovation = await RegistrationService.isFutureInnovationStudent(studentId);

        if (studentId !== this.studentId) return;

        this.studentInfo = studentInfo;
        this.isFutureInnovationStudent = !!(futureInnovation && futureInnovation.status);
        this.activeStudentId = studentId;
        this.registrationKey += 1;
        this.syncQuery();
      } catch (error) {
        this.error = true;
        if (error.response && error.response.data && error.response.data.message) {
          this.$toasted.error(JSON.stringify(error.response.data.message), {
            duration: 0,
          });
        }
      } finally {
        this.loading = false;
      }
    },

    clear_student() {
      this.activeStudentId = null;
      this.studentInfo = null;
      this.isFutureInnovationStudent = false;
      this.error = false;
    },

    fetchWhenStudentIdReady() {
      if (
        this.studentId &&
        String(this.studentId).length == 8 &&
        this.selected_year &&
        this.selected_semester
      ) {
        this.fetch_student();
      }
    },

    async fetchInit() {
      this.load_year();
      this.studentIdInput = this.$route.query.student_id || "";
      this.selected_year = this.$route.query.year ? String(this.$route.query.year) : null;
      this.selected_semester = this.$route.query.semester || null;

      if (this.studentIdInput && this.selected_year && this.selected_semester) {
        await this.$nextTick();
        await this.fetch_student();
      }
    },
  },
  watch: {
    studentIdInput: {
      handler: function () {
        this.clear_student();
        this.fetchWhenStudentIdReady();
      },
    },
  },
  mounted() {
    this.fetchInit();
  },
};
</script>
